import acc from "accounting"
import { Col, Flex, Row, Space, Tooltip, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import Button from "../../../Components/Button"
import Private from "../../../Components/Private"
import withPermission from "../../../hoc/withPermission"
import { useExternalDemoMode } from "../../../hooks/useExternalDemoMode"
import { highAccessRoles } from "../../../Utils/permission"
import { AddFundsModal } from "../../Balance/components/AddFundsModal"
import BalanceScale from "../../Balance/components/BalanceScale"

const { Text } = Typography

const PermissionButton = withPermission(Button, highAccessRoles)

const CurrentBalance = ({
  businessAccount,
  balance,
  ba_role,
  addFundsRef,
  scaleRef,
  isAdmin,
  userInfo,
  isMobile
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const externalDemoMode = useExternalDemoMode()

  const businessAccountRole = ba_role
  const hasBalanceManagementAccess = highAccessRoles.includes(Number(businessAccountRole))
  const has_bussiness_account = balance?.enable_to_topup

  const amount = balance?.amount ?? 0
  const abs = Math.abs(amount)
  const num = acc.formatNumber(abs, 2, ",", ".")

  const formattedAmount = amount < 0 ? `-$${num}` : `$${num}`
  const isDemoModeBlocked =
    !userInfo?.new_onboarding_finished && userInfo?.next_step < 8
  const isAddFundsDisabled =
    !hasBalanceManagementAccess &&
    (!has_bussiness_account ||
      externalDemoMode ||
      userInfo?.is_loyalty_billing ||
      isDemoModeBlocked)
 
  const handleAddFunds = () => {
    if (isAdmin) return
    navigate(pathname + "?open_add_funds=true")
  }

  const getButtonDisabledReason = () => {
    if (!has_bussiness_account)
      return t("You need to create a Business Account before adding funds.")
    if (userInfo?.is_loyalty_billing)
      return t(
        "Adding funds is disabled because your account has been moved to a new billing system"
      )
    return null
  }

  return (
    <>
      <Row>
        <Col flex={"1 1 300px"}>
          <Space size={8} direction={"vertical"} style={{ paddingBottom: isMobile && 16 }}>
            <Text strong>{t("Current balance")}</Text>
            <Text type="secondary">
              {t("This is the current balance in your Chargeback account.")}
            </Text>
          </Space>
        </Col>

        <Col flex={"0 1 431px"}>
          <Flex gap={12} vertical style={{ padding: 3 }}>
            <Flex vertical gap={4}>
              <Text type="secondary">{t("Balance")}</Text>
              <Text strong style={{ fontSize: 24, lineHeight: "34px" }}>
                <Private>{formattedAmount}</Private>
              </Text>
            </Flex>
            <div ref={addFundsRef} style={{ width: "fit-content"}}>
           {!isAdmin && !isMobile && (
            <Tooltip
                trigger={isAddFundsDisabled ? "hover" : null}
                title={getButtonDisabledReason()}
              >
              <PermissionButton
                    type="primary"
                    onClick={handleAddFunds}
                    className="flex"
                    text={t("Add funds")}
                disabled={
                  isAdmin || isAddFundsDisabled
                }
                role={businessAccountRole}
              />
            </Tooltip>
          )}
          </div>
          </Flex>

          <div ref={scaleRef}>
            <BalanceScale balance={balance} isMobile={isMobile} />

            <Flex vertical gap={4} style={{ marginTop: 16 }}>
              <Flex justify={"space-between"}>
                <Text type="secondary" strong style={{ fontSize: 12 }}>
                  {t("Critical")}
                </Text>
                <Text strong style={{ fontSize: 12 }}>
                  <Private>{`$${acc.formatNumber(balance?.["critical_level"], 2, ",", ".")}`}</Private>
                </Text>
              </Flex>
              <Flex justify={"space-between"}>
                <Text type="secondary" strong style={{ fontSize: 12 }}>
                  {t("Minimum")}
                </Text>
                <Text strong style={{ fontSize: 12 }}>
                  <Private>{`$${acc.formatNumber(balance?.["calculated_min_amount"], 2, ",", ".")}`}</Private>
                </Text>
              </Flex>

              <Flex justify={"space-between"}>
                <Text type="secondary" strong style={{ fontSize: 12 }}>
                  {t("Recommended")}
                </Text>
                <Text strong style={{ fontSize: 12 }}>
                  <Private>{`$${acc.formatNumber(balance?.["calculated_recommended"], 2, ",", ".")}`}</Private>
                </Text>
              </Flex>
            </Flex>
          </div>
        </Col>
        {!isAdmin && isMobile &&(
          <PermissionButton
            type="primary"
            size="large"
            onClick={handleAddFunds}
            className="flex"
            text={t("Add funds")}
            style={{ width: "100%", justifyContent: "center",marginTop: 16}}
              disabled={
                isAdmin || isAddFundsDisabled
              }
              role={businessAccountRole}
            />)}
      </Row>

      <AddFundsModal />
    </>
  )
}

const mapStateToProps = (state) => ({
  isMobile: state.nav.isMobile,
  isAdmin: state.auth.isAdmin,
  ba_role: state.auth.userInfo?.ba_role?.id,
  businessAccount: state.user.businessAccount,
  balance: state.balance.balance,
  userInfo: state.auth.userInfo
})

export default connect(mapStateToProps, null)(CurrentBalance)
